import React, { useState, useEffect } from 'react';
import { abdmApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  ShieldCheck, Gear, CheckCircle, XCircle, Users, Fingerprint,
  Link, Warning, ChartBar, ArrowRight, Lock,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ABDMPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [nhcxSettings, setNhcxSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.roles?.includes('hospital_admin') || user?.role === 'hospital_admin';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [dashRes, settRes, nhcxRes] = await Promise.all([
        abdmApi.dashboard(), abdmApi.getSettings(), abdmApi.getNhcxSettings()
      ]);
      setDashboard(dashRes.data);
      setSettings(settRes.data);
      setNhcxSettings(nhcxRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveAbdmSettings = async () => {
    setSaving(true);
    try {
      await abdmApi.updateSettings(settings);
      toast.success('ABDM settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveNhcxSettings = async () => {
    setSaving(true);
    try {
      await abdmApi.updateNhcxSettings(nhcxSettings);
      toast.success('NHCX settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" /></div>;

  const d = dashboard || {};

  return (
    <div className="space-y-5" data-testid="abdm-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-900 via-slate-900 to-blue-900 rounded-2xl p-6 text-white" data-testid="abdm-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-teal-500/20 flex items-center justify-center border border-teal-500/30">
              <Fingerprint className="h-6 w-6 text-teal-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="abdm-heading">ABDM Integration</h1>
              <p className="text-sm text-slate-300 mt-0.5">Ayushman Bharat Digital Mission &mdash; ABHA Linkage & NHCX Exchange</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className={`${d.abdm_enabled ? 'bg-teal-500/20 text-teal-300 border-teal-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'} border text-xs`}>
              ABDM {d.abdm_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Badge className={`${d.nhcx_enabled ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'} border text-xs`}>
              NHCX {d.nhcx_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        {[
          { key: 'dashboard', label: 'Dashboard', icon: ChartBar },
          { key: 'abdm-settings', label: 'ABDM Setup', icon: Gear },
          { key: 'nhcx-settings', label: 'NHCX Setup', icon: ArrowRight },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-teal-500 text-teal-700 bg-teal-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Total Patients', value: d.total_patients || 0, icon: Users, color: 'text-slate-700', bg: 'bg-slate-50' },
              { label: 'ABHA Linked', value: d.abha_linked || 0, icon: Link, color: 'text-teal-600', bg: 'bg-teal-50' },
              { label: 'ABHA Verified', value: d.abha_verified || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Link Rate', value: `${d.abha_link_rate || 0}%`, icon: ChartBar, color: 'text-blue-600', bg: 'bg-blue-50' },
            ].map((s, i) => (
              <Card key={i} className={`${s.bg} border-0 shadow-sm`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{s.label}</p>
                      <p className={`text-2xl font-bold font-['Outfit'] mt-1 ${s.color}`}>{s.value}</p>
                    </div>
                    <s.icon className={`h-6 w-6 ${s.color}`} weight="duotone" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Claims</p>
                <p className="text-3xl font-bold font-['Outfit'] text-slate-800 mt-1">{d.total_claims || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase font-semibold">ABHA-Linked Claims</p>
                <p className="text-3xl font-bold font-['Outfit'] text-teal-600 mt-1">{d.abha_claims || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase font-semibold">NHCX Submitted</p>
                <p className="text-3xl font-bold font-['Outfit'] text-blue-600 mt-1">{d.nhcx_submitted || 0}</p>
                <div className="flex justify-center gap-3 mt-1 text-[10px]">
                  <span className="text-emerald-600">{d.nhcx_success || 0} success</span>
                  <span className="text-red-500">{d.nhcx_failed || 0} failed</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ABDM Settings Tab */}
      {tab === 'abdm-settings' && settings && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="abdm-settings-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                <Fingerprint className="h-4 w-4 text-teal-500" /> ABDM Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-teal-50 border border-teal-100">
                <div>
                  <p className="text-sm font-medium text-slate-700">Enable ABDM</p>
                  <p className="text-[10px] text-slate-500">Activate ABHA linkage for this hospital</p>
                </div>
                <Switch checked={settings.enabled} onCheckedChange={v => setSettings(s => ({...s, enabled: v}))}
                  data-testid="toggle-abdm" disabled={!isAdmin} />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-700">Sandbox Mode</p>
                  <p className="text-[10px] text-slate-500">Use ABDM sandbox for testing</p>
                </div>
                <Switch checked={settings.sandbox_mode} onCheckedChange={v => setSettings(s => ({...s, sandbox_mode: v}))}
                  disabled={!isAdmin} />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-700">ABHA Mandatory</p>
                  <p className="text-[10px] text-slate-500">Require ABHA number for claim registration</p>
                </div>
                <Switch checked={settings.abha_mandatory} onCheckedChange={v => setSettings(s => ({...s, abha_mandatory: v}))}
                  disabled={!isAdmin} />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-700">Duplicate ABHA Check</p>
                  <p className="text-[10px] text-slate-500">Prevent same ABHA linked to multiple patients</p>
                </div>
                <Switch checked={settings.duplicate_check !== false} onCheckedChange={v => setSettings(s => ({...s, duplicate_check: v}))}
                  disabled={!isAdmin} />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-700">Mask ABHA in UI</p>
                  <p className="text-[10px] text-slate-500">Display ABHA as XX-****-****-XXXX</p>
                </div>
                <Switch checked={settings.mask_abha_ui !== false} onCheckedChange={v => setSettings(s => ({...s, mask_abha_ui: v}))}
                  disabled={!isAdmin} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                <Lock className="h-4 w-4 text-amber-500" /> ABDM API Credentials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">Get credentials from <a href="https://sandbox.abdm.gov.in" target="_blank" rel="noreferrer" className="underline font-semibold">ABDM Sandbox</a> or NHA for production.</p>
              </div>
              <div><Label>Client ID</Label>
                <Input value={settings.client_id || ''} onChange={e => setSettings(s => ({...s, client_id: e.target.value}))}
                  placeholder="Your ABDM Client ID" disabled={!isAdmin} data-testid="abdm-client-id" /></div>
              <div><Label>Client Secret</Label>
                <Input type="password" value={settings.client_secret || ''} onChange={e => setSettings(s => ({...s, client_secret: e.target.value}))}
                  placeholder="Enter to update" disabled={!isAdmin} /></div>
              <div><Label>Bridge URL</Label>
                <Input value={settings.bridge_url || ''} onChange={e => setSettings(s => ({...s, bridge_url: e.target.value}))}
                  placeholder="https://your-bridge.hospital.com" disabled={!isAdmin} /></div>
              <div><Label>Callback URL</Label>
                <Input value={settings.callback_url || ''} onChange={e => setSettings(s => ({...s, callback_url: e.target.value}))}
                  placeholder="https://your-callback.hospital.com/abdm" disabled={!isAdmin} /></div>

              {isAdmin && (
                <Button onClick={saveAbdmSettings} disabled={saving}
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 text-white" data-testid="save-abdm-btn">
                  {saving ? 'Saving...' : 'Save ABDM Settings'}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* NHCX Settings Tab */}
      {tab === 'nhcx-settings' && nhcxSettings && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="nhcx-settings-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                <ArrowRight className="h-4 w-4 text-blue-500" /> NHCX Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100">
                <div>
                  <p className="text-sm font-medium text-slate-700">Enable NHCX</p>
                  <p className="text-[10px] text-slate-500">Activate claim exchange via NHCX</p>
                </div>
                <Switch checked={nhcxSettings.enabled} onCheckedChange={v => setNhcxSettings(s => ({...s, enabled: v}))}
                  data-testid="toggle-nhcx" disabled={!isAdmin} />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-700">Sandbox Mode</p>
                  <p className="text-[10px] text-slate-500">Use NHCX sandbox for testing</p>
                </div>
                <Switch checked={nhcxSettings.sandbox_mode} onCheckedChange={v => setNhcxSettings(s => ({...s, sandbox_mode: v}))}
                  disabled={!isAdmin} />
              </div>
              <div><Label>Participant Code</Label>
                <Input value={nhcxSettings.participant_code || ''} onChange={e => setNhcxSettings(s => ({...s, participant_code: e.target.value}))}
                  placeholder="Hospital NHCX Participant Code" disabled={!isAdmin} data-testid="nhcx-participant" /></div>
              <div><Label>Registry URL</Label>
                <Input value={nhcxSettings.registry_url || ''} onChange={e => setNhcxSettings(s => ({...s, registry_url: e.target.value}))}
                  placeholder="https://nhcx-registry.nha.gov.in" disabled={!isAdmin} /></div>

              {isAdmin && (
                <Button onClick={saveNhcxSettings} disabled={saving}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white" data-testid="save-nhcx-btn">
                  {saving ? 'Saving...' : 'Save NHCX Settings'}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-blue-800 mb-3">NHCX Claim Exchange Readiness</h3>
              <div className="space-y-2">
                {[
                  { label: 'Pre-Auth Submission', ready: true },
                  { label: 'Claim Submission', ready: true },
                  { label: 'Query Response Handling', ready: true },
                  { label: 'Denial/Deficiency Response', ready: true },
                  { label: 'Settlement Tracking', ready: true },
                  { label: 'Acknowledgment Tracking', ready: true },
                  { label: 'Resubmission Workflow', ready: true },
                  { label: 'Payer Endpoint Config', ready: nhcxSettings.configured },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 text-xs p-1.5 rounded ${item.ready ? 'text-emerald-700' : 'text-slate-500'}`}>
                    {item.ready ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" weight="fill" /> : <XCircle className="h-3.5 w-3.5 text-slate-400" />}
                    {item.label}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ABDMPage;
